import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const articles = getArticlesByCategory(slug || '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 爱情岛论坛独家提供优质内容资源`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', category.description);
      }
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) {
        metaKw.setAttribute(
          'content',
          `爱情岛论坛${category.name},爱情岛独家资源,爱情岛论坛社区`
        );
      }
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
            <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
            分类未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            您访问的分类不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const otherCategories = categories.filter((c) => c.slug !== slug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${category.icon} text-primary-500 text-lg`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {category.name}
              </h1>
            </div>
            <p className="text-sm md:text-base text-foreground-500 ml-[52px]">
              {category.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200/70">
            {otherCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-background-200 hover:text-foreground-800 transition-colors whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles Grid */}
          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="group bg-background-100/70 rounded-lg overflow-hidden hover:bg-background-100 transition-colors"
                >
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </Link>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="text-xs text-foreground-400">
                        {article.publishedAt}
                      </span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400">
                        {article.author}
                      </span>
                    </div>
                    <Link to={`/article/${article.id}`} className="block mb-2">
                      <h2 className="text-base md:text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.readCount > 10000
                          ? `${(article.readCount / 10000).toFixed(1)}万`
                          : article.readCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i>
                        {article.likeCount}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}